/**
 * shared/feedback.ts —— 加载态 / 错误态（二期新增，一期明确不做）
 * 出处：06_二期技术方案.md §三之五 风险①「改成 await fetch 还要加载入态」
 *
 * ## 为什么要分两种形态（2026-08-13 拍板）
 * | 场景 | 形态 | 理由 |
 * | --- | --- | --- |
 * | 首次进页（内容区是空的） | **骨架块** | 空白区会让人以为坏了；骨架同时预告了版式 |
 * | 页内刷新（写操作后重拉） | **顶部进度条 + 旧内容变淡** | 已经有内容了还闪成骨架，观感倒退，且会丢滚动位置 |
 *
 * ## 三条纪律
 * 1. **只有 router/render.ts 调这里**。页面模块里不许自己写 loading——
 *    一旦分散，就会出现「有的页有加载态、有的页白屏」这种说不清的不一致。
 * 2. **骨架形态跟着页面版式走**（矩阵 / 表格 / 卡片三种），不要所有页面共用一个转圈。
 * 3. **不做循环动画**（红线 1）。骨架用的是【单向一次性的微光扫过】不是呼吸；
 *    `prefers-reduced-motion` 下完全静止，见 styles/16_loading.css。
 */
import { EIC, IC } from './icons.js';
import { emptyHtml } from './common.js';

/** 骨架形态。新增页面时挑一个最接近的，不要新造 */
export type SkeletonKind = 'matrix' | 'table' | 'cards' | 'form' | 'detail';

const bar = (w: string, h = '14px') => `<span class="sk-bar" style="width:${w};height:${h}"></span>`;
const rows = (n: number, html: string) => Array.from({ length: n }, () => html).join('');

/**
 * 骨架屏。
 * ⚠ 行数是照着真实版式给的（矩阵 13 队、卡片 9 张）——数量差太多会在加载完那一刻跳一下。
 */
export function skeletonHtml(kind: SkeletonKind): string {
  const head = `<div class="sk-head">${bar('180px', '22px')}${bar('96px', '22px')}</div>`;
  if (kind === 'matrix') {
    return `<div class="sk" aria-busy="true" aria-label="正在加载">
      ${head}
      <div class="sk-hero">${rows(3, `<div class="sk-hero-c">${bar('64%', '13px')}${bar('44%', '30px')}</div>`)}</div>
      <div class="sk-tbl">${rows(13, `<div class="sk-tr">${bar('100%')}</div>`)}</div>
    </div>`;
  }
  if (kind === 'table') {
    return `<div class="sk" aria-busy="true" aria-label="正在加载">
      ${head}<div class="sk-tbl">${rows(10, `<div class="sk-tr">${bar('100%')}</div>`)}</div>
    </div>`;
  }
  if (kind === 'cards') {
    return `<div class="sk" aria-busy="true" aria-label="正在加载">
      ${head}<div class="sk-grid">${rows(9, `<div class="sk-card">${bar('100%', '112px')}${bar('72%')}${bar('48%')}</div>`)}</div>
    </div>`;
  }
  if (kind === 'form') {
    return `<div class="sk" aria-busy="true" aria-label="正在加载">
      ${head}<div class="sk-form">${rows(4, `<div class="sk-fld">${bar('88px')}${bar('100%', '76px')}</div>`)}</div>
    </div>`;
  }
  return `<div class="sk" aria-busy="true" aria-label="正在加载">
    ${head}
    <div class="sk-hero">${rows(2, `<div class="sk-hero-c">${bar('56%', '13px')}${bar('40%', '30px')}</div>`)}</div>
    <div class="sk-tbl">${rows(6, `<div class="sk-tr">${bar('100%')}</div>`)}</div>
  </div>`;
}

/**
 * 错误态。
 * ⚠ 【可重试的才给重试按钮】。版本冲突、权限不足重试一百次也是同样结果，
 *   给了按钮只会让人反复点、以为是系统抽风。判据见 api/client.ts 的 isRetryable()。
 */
export function errorHtml(msg: string, retryable: boolean): string {
  const action = retryable
    ? `<button class="btn primary" data-retry>${IC.check}<span>重试</span></button>`
    : '';
  return emptyHtml(EIC.flag, '加载失败', msg, action);
}

/* ---------- 顶部进度条：页内刷新时用，不替换已有内容 ---------- */

let barEl: HTMLElement | null = null;
let hideTimer: number | null = null;

export function startProgress(): void {
  if (!barEl) {
    barEl = document.createElement('div');
    barEl.className = 'topbar';
    barEl.setAttribute('role', 'status');
    barEl.setAttribute('aria-label', '正在加载');
    document.body.appendChild(barEl);
  }
  if (hideTimer !== null) {
    clearTimeout(hideTimer);
    hideTimer = null;
  }
  barEl.classList.remove('done');
  // 强制回流，让下一帧的 running 类真的触发过渡（去掉这行进度条不会动）
  void barEl.offsetWidth;
  barEl.classList.add('running');
  document.body.classList.add('busy');
}

export function endProgress(): void {
  document.body.classList.remove('busy');
  if (!barEl) return;
  barEl.classList.add('done');
  barEl.classList.remove('running');
  hideTimer = window.setTimeout(() => {
    barEl?.classList.remove('done');
    hideTimer = null;
  }, 240);
}

/** 「接口还没接」的占位。⚠ W2 接完对应模块后【必须删掉对它的调用】，别留成永久占位 */
export function notReadyHtml(module: string, api: string): string {
  return emptyHtml(
    EIC.doc,
    `${module}｜接口尚未接入`,
    `本页的渲染将接 ${api}，随 W2 该模块一起实现。当前只验证路由、加载态与错误态。`,
    '',
  );
}

/** 无当前周期的空态。⚠ 这是【正常业务态】不是错误，文案里不许出现「错误 / 异常」（红线 3.5） */
export function noPeriodHtml(hint: string): string {
  // ⚠ 不要在这里再 esc(hint)：emptyHtml 内部已经转义了 title 与 desc，套两层会把中文引号变成 &quot;
  return emptyHtml(EIC.cal, '当前没有进行中的周期', hint, '');
}
